import * as React from 'react';
import { Redirect, Route, useHistory, useLocation, useRouteMatch, Switch } from 'react-router';
import { Menu, Typography, Divider, Alert } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { ReactComponent as WarningIcon } from '../../images/icon-warning.svg';
import { ManagePermissions } from '../permissions/ManagePermissions';
import { ManageOwnership } from '../entity/ownership/ManageOwnership';
import { ManageIdentities } from '../identity/ManageIdentities';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import { UnauthorisedPage } from '../shared/UnauthorisedPage';
import { useUserContext } from '../context/useUserContext';
import { useTranslation } from '../../i18n/useTranslation';
import { ManageViews } from '../entity/view/ManageViews';
import { ANTD_GRAY } from '../entity/shared/constants';
import { Preferences } from './Preferences';

const PageContainer = styled.div`
    display: flex;
    background-color: #ffffff;
`;

const SettingsBarContainer = styled.div`
    padding-top: 16px;
    min-height: 100vh;
    border-right: 1px solid ${ANTD_GRAY[5]};

    @media (max-width: 992px) {
        border-right: 0;
        min-height: auto;
        margin-bottom: 8px;
        border-bottom: 1px solid ${ANTD_GRAY[5]};
    }
`;

const SettingsBarHeader = styled.div`
    && {
        padding: 0 24px;
    }
    margin-bottom: 16px;
`;

const ThinDivider = styled(Divider)`
    margin: 0px;
`;

const ItemTitle = styled.span`
    margin-left: 8px;
`;

/**
 * URL Paths for each settings page.
 */
const PATHS = [
    { path: 'identities-debug', content: <ManageIdentities /> },
    { path: 'permissions-debug ', content: <ManagePermissions /> },
    { path: 'ownership', content: <ManageOwnership /> },
    { path: 'preferences-debug', content: <Preferences /> },
    { path: 'views', content: <ManageViews /> },
];

/**
 * The default selected path
 */
const DEFAULT_PATH = PATHS[2];

export const SettingsPage = () => {
    const { path, url } = useRouteMatch();
    const { pathname } = useLocation();
    const history = useHistory();
    const { t } = useTranslation();
    const subRoutes = PATHS.map((p) => p.path.replace('/', ''));
    const currPathName = pathname.replace(path, '');
    const trimmedPathName = currPathName.endsWith('/') ? pathname.slice(0, pathname.length - 1) : currPathName;
    const splitPathName = trimmedPathName.split('/');
    const providedPath = splitPathName[1];
    const activePath = subRoutes.includes(providedPath) ? providedPath : DEFAULT_PATH.path.replace('/', '');

    const me = useUserContext();

    const canManageOwnershipTypes = me && me?.platformPrivileges?.manageOwnershipTypes;
    const canViewOwnershipTypes = me && me?.platformPrivileges?.viewOwnershipTypes;

    const canAccessSettings = canManageOwnershipTypes || canViewOwnershipTypes;

    if (!me?.urn) {
        return <ComponentSpinner />;
    }

    if (!canAccessSettings) {
        return <UnauthorisedPage />;
    }

    return (
        <>
            <Alert
                showIcon
                type="warning"
                icon={<WarningIcon />}
                className="ma-2 f-alert-warning"
                message={t(
                    'System Update: EDC permissions have been migrated to the Roles module to simplify and streamline access management.',
                )}
            />
            <PageContainer className="settings-page f-flex-column-xs">
                <SettingsBarContainer>
                    <SettingsBarHeader>
                        <Typography.Title level={2} className="f-page-heading mb-0">
                            {t('Settings')}
                        </Typography.Title>
                        <Typography.Paragraph className="f-text-content mt-1">
                            {t('Manage your Data Catalog settings.')}
                        </Typography.Paragraph>
                    </SettingsBarHeader>
                    <ThinDivider />
                    <Menu
                        mode="inline"
                        selectable={false}
                        role="list"
                        className="f-width-100-xs"
                        selectedKeys={[activePath]}
                        style={{ width: 272, marginTop: 8 }}
                        onClick={(newPath) => {
                            history.replace(`${url}/${newPath.key}`);
                        }}
                    >
                        <Menu.ItemGroup title={t('Manage')}>
                            <Menu.Item key="ownership" aria-label={t('Ownership Types')} tabIndex={0} role="listitem">
                                <TeamOutlined /> <ItemTitle>{t('Ownership Types')}</ItemTitle>
                            </Menu.Item>
                        </Menu.ItemGroup>
                    </Menu>
                </SettingsBarContainer>
                <Switch>
                    <Route exact path={path}>
                        <Redirect to={`${pathname}${pathname.endsWith('/') ? '' : '/'}${DEFAULT_PATH.path}`} />
                    </Route>
                    {PATHS.map((p) => (
                        <Route path={`${path}/${p.path.replace('/', '')}`} render={() => p.content} key={p.path} />
                    ))}
                    <Route render={() => <Redirect to={`${url}/${DEFAULT_PATH.path}`} />} />
                </Switch>
            </PageContainer>
        </>
    );
};
